import { backendUrl } from "core/constants";
import { nhost } from "nhost";
import { startTransition, useEffect, useState } from "react";

/**
 * A URL the Microsoft Office viewer can fetch a document on.
 *
 * That viewer takes a URL and fetches it from MICROSOFT'S servers, then fetches
 * it again on later renders, so it needs a link that is reachable without a
 * session and stays reachable. A presigned storage URL is neither: it lives 30
 * seconds and only the browser holding the session can mint one.
 *
 * The backend mints the link instead. It first checks, with the caller's own
 * token, that they may read the file — so the row-level permissions decide, not
 * the backend — and then serves the bytes on a signed URL good for two hours.
 *
 * Returns `undefined` while signing, and `null` if the backend refuses (no
 * access, or the service is down), which the caller shows as "no preview".
 */
const useOfficeFile = (fileId?: string) => {
	const [url, setUrl] = useState<string | undefined | null>(undefined);

	useEffect(() => {
		setUrl(undefined);
		if (!fileId) return;

		const sign = async () => {
			const token = nhost.auth.getAccessToken();
			if (!token) return null;
			const response = await fetch(
				`${backendUrl}/office/sign?fileId=${encodeURIComponent(fileId)}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (!response.ok) return null;
			const { url } = await response.json();
			return typeof url === "string" ? url : null;
		};

		let live = true;
		sign()
			.catch(() => null)
			// Ignore a resolution that belongs to a file we have moved on from.
			.then((signed) => {
				if (live) startTransition(() => setUrl(signed));
			});
		return () => {
			live = false;
		};
	}, [fileId]);

	return url;
};

export default useOfficeFile;
